/**
 * More info on CSS transform!
 * https://www.w3.org/TR/css-transforms-1/#intro
 * https://www.sitepoint.com/how-to-use-2d-transformation-functions-in-css/ <- The 'Matrix Multiplication and Transforms' part
 *
 * Great reference:
 * https://github.com/hrvojekindl/pinch-zoom/blob/master/dist/pinch-zoom.js
 * https://medium.com/@auchenberg/detecting-multi-touch-trackpad-gestures-in-javascript-a2505babb10e
 */

export type TransformationMatrix2D = [
  [number, number, number],
  [number, number, number],
  [number, number, number],
];

export interface TransformationMatrix2DParams {
  scaleX: number;
  scaleY: number;
  translateX: number;
  translateY: number;
  skewX: number;
  skewY: number;
}

export const getIdentityTransformationMatrix2D = (): TransformationMatrix2D => {
  return [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
};

export const getTransformationMatrix2DParams = (
  mat: TransformationMatrix2D,
): TransformationMatrix2DParams => {
  return {
    scaleX: mat[0][0],
    scaleY: mat[1][1],
    translateX: mat[0][2],
    translateY: mat[1][2],
    skewX: mat[0][1],
    skewY: mat[1][0],
  };
};

export const matrixMultiplication = (
  mat1: TransformationMatrix2D,
  mat2: TransformationMatrix2D,
): TransformationMatrix2D => {
  const result: TransformationMatrix2D = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];

  // for every cell (i,j) in result, where (i,j) means row (i+1), column (j+1), we sum up
  // the products of mat1[i][k] and mat2[k][i] for all 0 <= k <= 2,
  // (all the entries in row (i+1) of mat1, and all the entries in column (j+1) of mat2)
  for (let i = 0; i < 3; ++i) {
    for (let j = 0; j < 3; ++j) {
      let sum = 0;
      for (let k = 0; k < 3; ++k) {
        sum += mat1[i][k] * mat2[k][j];
      }
      result[i][j] = sum;
    }
  }

  return result;
};

const getScalingMatrix = (scaleX: number, scaleY: number): TransformationMatrix2D => {
  return [
    [scaleX, 0, 0],
    [0, scaleY, 0],
    [0, 0, 1],
  ];
};

const getTranslationMatrix = (translateX: number, translateY: number): TransformationMatrix2D => {
  return [
    [1, 0, translateX],
    [0, 1, translateY],
    [0, 0, 1],
  ];
};

export const applyScalingOnPoint = (
  previousMat: TransformationMatrix2D,
  scaling: number,
  x: number,
  y: number,
): TransformationMatrix2D => {
  const previousMatParams = getTransformationMatrix2DParams(previousMat);
  const afterUntranslating = matrixMultiplication(
    getIdentityTransformationMatrix2D(),
    getTranslationMatrix(x, y),
  );
  const afterCurrentTranslate = matrixMultiplication(
    afterUntranslating,
    getTranslationMatrix(previousMatParams.translateX, previousMatParams.translateY),
  );
  const afterScaling = matrixMultiplication(
    afterCurrentTranslate,
    getScalingMatrix(scaling, scaling),
  );
  const afterTranslating = matrixMultiplication(afterScaling, getTranslationMatrix(-x, -y));
  const afterCurrentScale = matrixMultiplication(
    afterTranslating,
    getScalingMatrix(previousMatParams.scaleX, previousMatParams.scaleY),
  );
  return afterCurrentScale;
};

export const applyTranslation = (
  previousMat: TransformationMatrix2D,
  x: number,
  y: number,
): TransformationMatrix2D => {
  const afterTranslating = matrixMultiplication(previousMat, getTranslationMatrix(x, y));
  return afterTranslating;
};
